import type { ReactNode } from 'react';

interface SectionProps {
  /** Section heading. Rendered as an `<h2>` — keep the page's `<h1>` outside. */
  title: string;
  /** Optional sub-heading; rendered muted under the title. */
  description?: string;
  /**
   * Optional badge after the title (typically a row count, e.g. `5`, or a
   * "filtered / total" string when a search is active), rendered smaller,
   * muted, and parenthesized (e.g. "(5)"). `ReactNode` so the caller can
   * pass numbers, strings, or composed JSX without prop-stuffing.
   */
  count?: ReactNode;
  /**
   * Optional toolbar slot floated to the right of the header — search
   * inputs, toggles, action buttons. Use for controls that belong to the
   * section's content (not page-level actions).
   */
  headerRight?: ReactNode;
  children: ReactNode;
}

/**
 * The card-section primitive used everywhere a screen needs grouped
 * content under a small heading. Replaces the two near-duplicates
 * (`FormSection` in `user-edit-form.tsx` and `DetailsSection` in the
 * user-detail route) so view ↔ edit stay visually in sync when a designer
 * tweaks padding, border radius, or header rhythm.
 *
 * Layout: rounded card on `bg-card` with `p-4` inset; header is a
 * flex-wrap row so the right-side toolbar drops below the title on narrow
 * viewports instead of overflowing. Children get `space-y-3` so multiple
 * field rows or sub-blocks have automatic vertical rhythm without each
 * caller re-declaring it.
 */
export function Section({ title, description, count, headerRight, children }: SectionProps) {
  return (
    <section className="space-y-3 rounded-md border bg-card p-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h2 className="text-sm font-semibold">
            {title}
            {count != null ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">({count})</span>
            ) : null}
          </h2>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {headerRight}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
